import React, { createContext, useContext, useCallback, useState, useEffect, useMemo } from 'react';
import { AppContext } from '../../window';
import { isEmailBounced } from '../../utils/emailStatus';
import { safeLocalStorage } from '../../utils/storage';

export enum ProfileCompletionStep {
  EMAIL_BOUNCED = 'email_bounced',
  EMAIL_VERIFICATION = 'email_verification',
  PUSH_NOTIFICATIONS = 'push_notifications',
  PROFILE_PICTURE = 'profile_picture',
  BASIC_INFO = 'basic_info',
  COMPLETE = 'complete'
}

interface ProfileCompletionContextType {
  currentStep: ProfileCompletionStep;
  missingBasicInfoFields: string[];
  isBasicInfoIncomplete: boolean;
  dismissedSteps: Set<string>;
  dismissStep: (step: ProfileCompletionStep) => void;
  undismissStep: (step: ProfileCompletionStep) => void;
}

const ProfileCompletionContext = createContext<ProfileCompletionContextType | null>(null);

interface ProfileCompletionProviderProps {
  children: React.ReactNode;
}

export const ProfileCompletionProvider: React.FC<ProfileCompletionProviderProps> = ({ 
  children
}) => {
  const appContext = useContext(AppContext);
  const user = appContext?.user;

  // Initialize with localStorage data synchronously to prevent flash
  const [dismissedSteps, setDismissedSteps] = useState<Set<string>>(() => {
    try {
      const savedDismissed = safeLocalStorage.getItem('dismissedProfileSteps');
      return savedDismissed ? new Set(JSON.parse(savedDismissed)) : new Set();
    } catch (e) {
      console.warn('Failed to parse dismissed profile steps from localStorage');
      return new Set();
    }
  });

  // Save to localStorage whenever dismissedSteps changes
  useEffect(() => {
    safeLocalStorage.setItem('dismissedProfileSteps', JSON.stringify(Array.from(dismissedSteps)));
  }, [dismissedSteps]);

  const dismissStep = useCallback((step: ProfileCompletionStep) => {
    setDismissedSteps(prev => {
      const newSet = new Set(prev);
      newSet.add(step);
      return newSet;
    });
  }, []);

  const undismissStep = useCallback((step: ProfileCompletionStep) => {
    setDismissedSteps(prev => {
      const newSet = new Set(prev);
      newSet.delete(step);
      return newSet;
    });
  }, []);

  const getCurrentStep = useCallback((): ProfileCompletionStep => {
    if (!user) {
      return ProfileCompletionStep.COMPLETE;
    }

    // Mail to the address hard-bounced: ask for a new one, whatever was dismissed
    if (isEmailBounced(user.credentials)) {
      return ProfileCompletionStep.EMAIL_BOUNCED;
    }

    if ((!user.credentials?.emailStatus || user.credentials?.emailStatus === 0) && !dismissedSteps.has(ProfileCompletionStep.EMAIL_VERIFICATION)) {
      return ProfileCompletionStep.EMAIL_VERIFICATION;
    }
    
    if ((!user.displayPicture || !user.displayPicture.id) && !dismissedSteps.has(ProfileCompletionStep.PROFILE_PICTURE)) {
      return ProfileCompletionStep.PROFILE_PICTURE;
    }

    const hasBasicInfo = Boolean(
      user.details?.sexuality && user.details?.sexuality !== '' && user.details?.sexuality !== 'select' &&
      user.details?.figure && user.details?.figure !== '' && user.details?.figure !== 'select' &&
      user.details?.haircolor && user.details?.haircolor !== '' && user.details?.haircolor !== 'select' &&
      user.details?.eyecolor && user.details?.eyecolor !== '' && user.details?.eyecolor !== 'select' &&
      user.description?.trim()
    );

    if (!hasBasicInfo && !dismissedSteps.has(ProfileCompletionStep.BASIC_INFO)) {
      return ProfileCompletionStep.BASIC_INFO;
    }

    // Show push notifications step if user hasn't enabled and hasn't dismissed
    // Browser support check is handled in the prompt when user tries to enable
    if (user.credentials?.pushStatus !== 1 && !dismissedSteps.has(ProfileCompletionStep.PUSH_NOTIFICATIONS)) {
      return ProfileCompletionStep.PUSH_NOTIFICATIONS;
    }
    
    return ProfileCompletionStep.COMPLETE;
  }, [user, dismissedSteps]);

  const getMissingBasicInfoFields = useCallback((): string[] => {
    if (!user) return [];
    
    const missing = [];
    
    if (!user.details?.sexuality || user.details?.sexuality === '' || user.details?.sexuality === 'select') {
      missing.push('sexuality');
    }
    
    if (!user.details?.figure || user.details?.figure === '' || user.details?.figure === 'select') {
      missing.push('figure');
    }
    
    if (!user.details?.haircolor || user.details?.haircolor === '' || user.details?.haircolor === 'select') {
      missing.push('haircolor');
    }
    
    if (!user.details?.eyecolor || user.details?.eyecolor === '' || user.details?.eyecolor === 'select') {
      missing.push('eyecolor');
    }
    
    if (!user.description?.trim()) {
      missing.push('description');
    }
    
    return missing;
  }, [user]);

  const currentStep = getCurrentStep();
  const missingBasicInfoFields = getMissingBasicInfoFields();
  const isBasicInfoIncomplete = currentStep === ProfileCompletionStep.BASIC_INFO;
  const value: ProfileCompletionContextType = useMemo(() => ({
    currentStep,
    missingBasicInfoFields,
    isBasicInfoIncomplete,
    dismissedSteps,
    dismissStep,
    undismissStep
  }), [currentStep, missingBasicInfoFields, isBasicInfoIncomplete, dismissedSteps, dismissStep, undismissStep]);

  return (
    <ProfileCompletionContext.Provider value={value}>
      {children}
    </ProfileCompletionContext.Provider>
  );
};

export const useProfileCompletion = (): ProfileCompletionContextType => {
  const context = useContext(ProfileCompletionContext);
  if (!context) {
    // Fallback behavior when used outside of provider (for standalone usage)
    return {
      currentStep: ProfileCompletionStep.COMPLETE,
      missingBasicInfoFields: [],
      isBasicInfoIncomplete: false,
      dismissedSteps: new Set(),
      dismissStep: () => {},
      undismissStep: () => {}
    };
  }
  return context;
};